/**
 * BuoyancyZone2D Component
 *
 * Defines a region that applies buoyancy forces to physics bodies (water, lava, etc).
 * Bodies entering the zone experience upward force proportional to submerged volume
 * and drag forces that slow movement.
 */

import {
  component,
  displayName,
  icon,
  color,
  category,
  allowMultiple,
  requireComponent,
  field,
  customEditor,
} from "../attributes";

@component("core.BuoyancyZone2D")
@customEditor("BuoyancyZone2DEditor")
@displayName("Buoyancy Zone 2D")
@icon("Waves")
@color("#3498db")
@category("Physics 2D")
@allowMultiple(false)
@requireComponent(["core.Transform"])
export class BuoyancyZone2D {
  /** Fluid density - higher values create stronger buoyancy (water ~1, lava ~3) */
  @field({ type: "f32", label: "Density", min: 0.1, step: 0.1 })
  density: number = 1.0;

  /** Linear drag coefficient - slows linear movement */
  @field({ type: "f32", label: "Linear Drag", min: 0, step: 0.1 })
  linearDrag: number = 3.0;

  /** Angular drag coefficient - slows rotation */
  @field({ type: "f32", label: "Angular Drag", min: 0, step: 0.1 })
  angularDrag: number = 1.0;

  /** Flow velocity - adds current/stream effect */
  @field({ type: "vec2", label: "Flow" })
  flow: { x: number; y: number } = { x: 0, y: 0 };

  /** Surface normal direction (normalized internally) - which way is "up" for buoyancy */
  @field({ type: "vec2", label: "Surface Normal" })
  surfaceNormal: { x: number; y: number } = { x: 0, y: 1 };

  // ─── Zone Shape ────────────────────────────────────────────────────────────

  /** Zone width (box shape) */
  @field({ type: "f32", label: "Width", min: 0.1, step: 0.1 })
  width: number = 10;

  /** Zone height (box shape) */
  @field({ type: "f32", label: "Height", min: 0.1, step: 0.1 })
  height: number = 5;

  /** Priority for overlapping zones */
  @field({ type: "i32", label: "Priority", min: 0, max: 100 })
  priority: number = 0;

  /** Which physics layers are affected */
  @field({ type: "i32", label: "Affected Layers", hidden: true })
  affectedLayers: number = 0xFFFF;
}
